import { Component, OnInit, NgZone, Optional } from "@angular/core";
import { InfinityUtil, FormItem, isIe } from 'src/infinity-ui';
import { ApiService }                   from './services/api.service';
import { ActionService, APPLIED_PAY_REDIRECTED_KEY } from './services/action.service';
import { ErrorService }                 from './services/error.service';
import { arcPathParams }                from "./templates/ARC.path-params";
import { arcRatingGroups }              from "./templates/ARC.rating-groups";
import { arcPaymentComplete, parseAppliedPayReturnParams } from "./data/ARC.payment-complete";
import { arcRegisterFormSummary }       from "./templates/ARC.register-summary";
import { getHeader }                   from "./templates/UMB.new-application-form";
import { ataStatic }                    from "./data/ata_static";
import { umbrellaVariables }            from "./variables";
import objectFitImages                  from 'object-fit-images';
import { normalizeParticipantRatingForIntake } from './services/participant-rating-normalizer';

const APPLICANT_EMAIL_VALUE_PATH = "email.value";
const APPLICANT_VERIFY_EMAIL_VALUE_PATH = "verifyEmail.value";
const RATING_BOUND_RECOVERY_MESSAGE = "Your session expired. Confirm the email on your application to receive a secure link. Your existing rating will be kept.";

@Component({
    selector: "app-root",
    template: `
        <infinity-item [item]="rootItem" [actions]="actions"></infinity-item>
        <div *ngIf="capabilityExpiryVisible" class="capability-expiry-overlay" role="presentation">
            <section class="capability-expiry-card" role="alertdialog" aria-modal="true" tabindex="-1"
                aria-labelledby="capability-expiry-title" aria-describedby="capability-expiry-message">
                <h2 id="capability-expiry-title">{{ capabilityWasRevoked ? 'This application link is no longer available' : capabilityHasExpired ? 'Your session has expired' : 'Your session is about to expire' }}</h2>
                <p id="capability-expiry-message" *ngIf="capabilityHasExpired">{{ capabilityRecoverySent ? capabilityRecoverySentMessage : capabilityRefreshMessage }}</p>
                <p *ngIf="!capabilityHasExpired">For your security, refresh your session to continue. {{ capabilityCountdownText }} remaining.</p>
                <label *ngIf="capabilityHasExpired && capabilityRecoveryRatingId > 0 && !capabilityRecoverySent" for="capability-recovery-email">Confirm the email on your application</label>
                <input *ngIf="capabilityHasExpired && capabilityRecoveryRatingId > 0 && !capabilityRecoverySent" #capabilityRecoveryEmail id="capability-recovery-email" type="email" autocomplete="email" [value]="currentApplicantEmail" />
                <button *ngIf="capabilityHasExpired && capabilityRecoveryRatingId > 0 && !capabilityRecoverySent" type="button" class="capability-expiry-continue" [disabled]="capabilityRefreshLoading"
                    (click)="requestRatingBoundRecovery(capabilityRecoveryEmail.value)">{{ capabilityRefreshLoading ? 'Sending…' : 'Email me a secure link' }}</button>
                <button *ngIf="!capabilityWasRevoked && capabilityRecoveryRatingId <= 0" type="button" class="capability-expiry-continue" [disabled]="capabilityRefreshLoading"
                    (click)="refreshCapabilitySession()">{{ capabilityRefreshLoading ? 'Refreshing…' : 'Refresh session' }}</button>
            </section>
        </div>`,
    styles: [ ":host { display: block; height: 100%; width: 100%; }" ]
})
export class AppComponent implements OnInit {
    private static readonly CAPABILITY_WARNING_SECONDS = 5 * 60;
    private capabilityExpiryInterval: any;
    private capabilityRefreshInProgress = false;
    private capabilityExpiryReloadStarted = false;
    private capabilityExpiryFocusReturn: HTMLElement | null = null;
    public capabilityHasExpired = false;
    public capabilityWasRevoked = false;
    public capabilityExpiryVisible = false;
    public capabilityRefreshLoading = false;
    public capabilityCountdownSeconds = 60;
    public capabilityRecoverySent = false;
    public capabilityRecoverySentMessage = "If the application can be recovered, a secure link will be sent to the email on file. Your existing rating and application details are preserved.";
    public capabilityRefreshMessage = "Refresh your session to continue. Your application details will remain on this page.";

    get capabilityCountdownText(): string {
        const seconds = Math.max(0, this.capabilityCountdownSeconds);
        if (seconds < 60) { return `${seconds} seconds`; }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = String(seconds % 60).padStart(2, "0");
        return `${minutes}:${remainingSeconds} minutes`;
    }

    get capabilityRecoveryRatingId(): number {
        return Number(this.util?.get?.("capabilityContext.participantRatingId") ||
            this.util?.get?.("participantRatingId") ||
            this.util?.get?.("participantRating.participantRatingId") || 0);
    }

    get currentApplicantEmail(): string {
        return String(sessionStorage.getItem("arc_capability_email") ||
            this.util?.get?.(APPLICANT_EMAIL_VALUE_PATH) || this.util?.get?.(APPLICANT_VERIFY_EMAIL_VALUE_PATH) || "");
    }
    public rootItem: FormItem = {
        type: "none",
        className: "fill",
        visible: true,
        items: [
            { type: "modal", itemId: "error", className: "fill abs-tl bg-255-255-255-5", visible: false },          // Error Modal Component
            { type: "spinner", itemId: "loading", className: "abs-tl align fill bg-255 trans-3 z-2", value: true }, // Loading Spinner Component
            {
                type: "none",
                itemId: "appliedPayRedirectModal",
                className: "applied-pay-redirect-modal abs-tl align fill bg-255-255-255-5 z-3",
                visible: false,
                label: `
                    <div class="applied-pay-redirect-card" role="status" aria-live="polite">
                        <div class="applied-pay-redirect-loader" aria-hidden="true"></div>
                        <h2>Redirecting to Applied Pay</h2>
                        <p>Please do not refresh, close this browser, or use the Back button.</p>
                        <p>You will be redirected to our secure Applied Pay payment page in a moment.</p>
                    </div>
                `
            },
            {
                type: "none",
                itemId: "capabilityExpiryModal",
                className: "fill abs-tl bg-255-255-255-5 z-5 capability-expiry-overlay",
                visible: false,
                label: ""
            }
        ]
    };
    public staticPrograms = {
        ata: ataStatic
    };
    public backendWaitTime = 30 * 1000;

    constructor(
        public util: InfinityUtil,
        public api: ApiService,
        public actions: ActionService,
        public errors: ErrorService,
        @Optional() private zone?: NgZone) { }

    /** QA console hook: set a simulated expiry and start the normal timer inside Angular's zone. */
    scheduleCapabilityExpiryTest(secondsUntilExpiry = 360): { secondsUntilExpiry: number; secondsUntilWarning: number } {
        const applicantEmail = sessionStorage.getItem("arc_capability_email") ||
            this.util.get(APPLICANT_EMAIL_VALUE_PATH) || this.util.get(APPLICANT_VERIFY_EMAIL_VALUE_PATH);
        const applicantProgram = sessionStorage.getItem("arc_capability_program") || this.util.get("programName");
        if (!this.api?.hasCapabilityToken?.() || !applicantEmail || !applicantProgram) {
            throw new Error("Complete the applicant details first so the page has a capability token, email, and program.");
        }

        const delay = Math.max(1, Math.floor(secondsUntilExpiry));
        const run = () => {
            sessionStorage.setItem("arc_capability_expires_at", String(Date.now() + delay * 1000));
            this.checkCapabilityExpiry();
        };
        if (this.zone) { this.zone.run(run); } else { run(); }
        return {
            secondsUntilExpiry: delay,
            secondsUntilWarning: Math.max(0, delay - AppComponent.CAPABILITY_WARNING_SECONDS)
        };
    }

    ngOnInit() {
        this.registerAppliedPayBackHandler();
        this.registerCapabilityExpiryHandler();
        void this.initializeAsync();
    }

    private registerCapabilityExpiryHandler(): void {
        window.addEventListener("pageshow", () => this.checkCapabilityExpiry());
        window.addEventListener("arc-capability-token-updated", () => this.checkCapabilityExpiry());
        window.addEventListener("arc-capability-expiry-prompt", () => {
            this.capabilityWasRevoked = false;
            this.capabilityHasExpired = true;
            this.capabilityRecoverySent = false;
            this.setCapabilityExpiryModal(true);
        });
        window.addEventListener("arc-capability-revoked", () => {
            this.capabilityWasRevoked = true;
            this.capabilityHasExpired = true;
            this.capabilityRecoverySent = false;
            this.capabilityRefreshMessage = this.capabilityRecoveryRatingId > 0
                ? RATING_BOUND_RECOVERY_MESSAGE
                : "This application link is no longer available. Please reopen the application from your link sender.";
            this.setCapabilityExpiryModal(true);
        });
        window.addEventListener("arc-rating-bound-recovery-required", () => {
            this.capabilityWasRevoked = false;
            this.capabilityHasExpired = true;
            this.capabilityRecoverySent = false;
            this.capabilityRefreshMessage = RATING_BOUND_RECOVERY_MESSAGE;
            this.setCapabilityExpiryModal(true);
        });
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") { this.checkCapabilityExpiry(); }
        });
        document.addEventListener("click", event => {
            const target = event.target as HTMLElement;
            if (target?.closest?.(".applied-pay-timeout-refresh")) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                void this.actions.retryAppliedPayAfterTimeout();
                return;
            }
            if (this.util.get("capabilityExpiryModal.visible")) {
                if (target?.closest?.(".capability-expiry-card")) { return; }
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
            }
        }, true);
        document.addEventListener("keydown", event => this.trapCapabilityExpiryFocus(event), true);
    }

    private trapCapabilityExpiryFocus(event: KeyboardEvent): void {
        if (!this.capabilityExpiryVisible) { return; }
        const dialog = document.querySelector<HTMLElement>(".capability-expiry-card");
        if (!dialog) { return; }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (event.key !== "Tab") { return; }

        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (!focusable.length) {
            event.preventDefault();
            dialog.focus();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
            event.preventDefault();
            first.focus();
        }
    }

    private checkCapabilityExpiry(): void {
        if (this.capabilityExpiryInterval) {
            clearTimeout(this.capabilityExpiryInterval);
            clearInterval(this.capabilityExpiryInterval);
            this.capabilityExpiryInterval = null;
        }
        const expiresAt = this.api?.getCapabilityExpiresAt?.();
        const applicantEmail = sessionStorage.getItem("arc_capability_email") ||
            this.util.get(APPLICANT_EMAIL_VALUE_PATH) || this.util.get(APPLICANT_VERIFY_EMAIL_VALUE_PATH);
        const applicantProgram = sessionStorage.getItem("arc_capability_program") || this.util.get("programName");
        if (!expiresAt || !this.api?.hasCapabilityToken?.() || !applicantEmail || !applicantProgram) {
            this.setCapabilityExpiryModal(false);
            return;
        }
        const remainingSeconds = Math.ceil((expiresAt - Date.now()) / 1000);
        if (remainingSeconds <= 0) {
            this.expireCapabilityAndReload();
            return;
        }
        if (remainingSeconds > AppComponent.CAPABILITY_WARNING_SECONDS) {
            this.setCapabilityExpiryModal(false);
            this.capabilityExpiryInterval = setTimeout(() => this.checkCapabilityExpiry(),
                (remainingSeconds - AppComponent.CAPABILITY_WARNING_SECONDS) * 1000);
            return;
        }

        this.capabilityHasExpired = false;
        this.setCapabilityExpiryModal(true);
        this.capabilityCountdownSeconds = Math.max(remainingSeconds, 0);
        this.capabilityExpiryInterval = setInterval(() => {
            const seconds = Math.max(Math.ceil((expiresAt - Date.now()) / 1000), 0);
            this.capabilityCountdownSeconds = seconds;
            if (seconds <= 0) {
                clearInterval(this.capabilityExpiryInterval);
                this.capabilityExpiryInterval = null;
                this.expireCapabilityAndReload();
            }
        }, 1000);
    }

    private expireCapabilityAndReload(): void {
        if (this.capabilityExpiryReloadStarted) { return; }
        this.capabilityExpiryReloadStarted = true;
        if (this.capabilityExpiryInterval) {
            clearTimeout(this.capabilityExpiryInterval);
            clearInterval(this.capabilityExpiryInterval);
            this.capabilityExpiryInterval = null;
        }
        this.capabilityHasExpired = true;
        if (this.capabilityRecoveryRatingId > 0) {
            this.capabilityRefreshMessage = RATING_BOUND_RECOVERY_MESSAGE;
            this.capabilityRecoverySent = false;
            this.api?.clearCurrentApplicantCapability?.();
            this.setCapabilityExpiryModal(true);
            return;
        }

        this.capabilityRefreshMessage = "Your session expired. Reloading the application.";
        this.setCapabilityExpiryModal(true);
        this.api?.clearCurrentApplicantCapability?.();
        window.onbeforeunload = null;
        this.reloadPage();
    }

    private async renewCapabilityForCurrentApplicant(): Promise<void> {
        if (this.capabilityRefreshInProgress) { return; }
        if (!this.api?.renewCapabilityForCurrentApplicant) { return; }
        this.capabilityRefreshInProgress = true;
        this.capabilityRefreshLoading = true;
        try {
            const refreshed = await this.api.renewCapabilityForCurrentApplicant();
            if (refreshed) {
                this.capabilityWasRevoked = false;
                this.capabilityHasExpired = false;
                this.capabilityRefreshMessage = "Refresh your session to continue. Your application details will remain on this page.";
                this.setCapabilityExpiryModal(false);
                this.util.setItemValue("error", "");
                this.checkCapabilityExpiry();
            } else {
                this.capabilityRefreshMessage = "We could not refresh your session. Please try again.";
            }
        } catch {
            this.capabilityRefreshMessage = "We could not refresh your session. Please try again.";
        } finally {
            this.capabilityRefreshLoading = false;
            this.capabilityRefreshInProgress = false;
        }
    }

    refreshCapabilitySession(): void {
        void this.renewCapabilityForCurrentApplicant();
    }

    async requestRatingBoundRecovery(email: string): Promise<void> {
        if (this.capabilityRefreshInProgress || this.capabilityRecoveryRatingId <= 0) { return; }
        this.capabilityRefreshInProgress = true;
        this.capabilityRefreshLoading = true;
        try {
            const requested = await this.api.requestRatingBoundIntakeRecovery(email);
            if (!requested) {
                this.capabilityRefreshMessage = "Enter the email address on your application and try again.";
                return;
            }
            this.capabilityRecoverySent = true;
        } catch {
            this.capabilityRefreshMessage = "We could not request a secure link. Check the email address and try again.";
        } finally {
            this.capabilityRefreshLoading = false;
            this.capabilityRefreshInProgress = false;
        }
    }

    private setCapabilityExpiryModal(visible: boolean): void {
        const item = this.util?.get?.("capabilityExpiryModal");
        if (!item) { return; }
        const wasVisible = this.capabilityExpiryVisible;
        if (visible && !wasVisible) {
            const activeElement = document.activeElement;
            this.capabilityExpiryFocusReturn = activeElement instanceof HTMLElement ? activeElement : null;
        }
        if (visible) { this.actions?.setAppliedPayLoading?.(false); }
        item.visible = visible;
        this.capabilityExpiryVisible = visible;
        this.util.set({ capabilityExpiryModal: item }, { batch: true });
        if (visible) {
            setTimeout(() => {
                if (!this.capabilityExpiryVisible) { return; }
                (document.querySelector<HTMLButtonElement>(".capability-expiry-continue") ||
                    document.querySelector<HTMLElement>(".capability-expiry-card"))?.focus();
            }, 0);
        } else if (wasVisible) {
            const focusReturn = this.capabilityExpiryFocusReturn;
            this.capabilityExpiryFocusReturn = null;
            setTimeout(() => {
                if (focusReturn?.isConnected) { focusReturn.focus(); }
            }, 0);
        }
    }

    // AC3: after redirecting to the Applied Pay Hosted Payment Page, pressing browser Back must always
    // show a brand-new blank submission form — never the in-flight/loading screen or the summary/review
    // page. A full-document redirect (window.location.href) means Back can restore this page from the
    // browser bfcache (event.persisted) without re-running Angular init, showing the stale loading/summary
    // DOM. The "appliedPay.redirected" marker (set in ActionService.redirectToPaymentPage) scopes this to
    // the Applied Pay HPP redirect only, so Applied Pay OFF behavior is unchanged. Works in Chrome/Edge.
    private registerAppliedPayBackHandler() {
        window.addEventListener("pageshow", (event: PageTransitionEvent) => this.handleAppliedPayPageShow(event));
    }

    // Real bfcache decision logic, split out so it can be unit-tested directly. Returns true when a
    // reload was triggered (bfcache restore of an Applied Pay redirect).
    handleAppliedPayPageShow(event: PageTransitionEvent): boolean {
        // Best-effort: sessionStorage can throw (private mode / disabled / blocked). Treat any
        // failure as "no marker" so a storage error can never crash this pageshow handler, which
        // runs on every page load. Mirrors the guard around the marker write in
        // ActionService.redirectToPaymentPage.
        let hasMarker: boolean;
        try {
            hasMarker = !!sessionStorage.getItem(APPLIED_PAY_REDIRECTED_KEY);
        } catch {
            return false;  // storage unavailable → not our redirect → do nothing
        }
        if (!hasMarker) {
            return false; // not our redirect → do nothing
        }
        try {
            sessionStorage.removeItem(APPLIED_PAY_REDIRECTED_KEY);
        } catch {
            // ignore — clearing the back-button freshness marker is best-effort.
        }
        // bfcache restore: force a reload so the app re-initializes to a fresh, blank form.
        // Non-bfcache Back is a normal load that already re-initializes, so nothing extra is needed.
        if (event.persisted) {
            this.reloadPage();
            return true;
        }
        return false;
    }

    // Seam so tests can assert the reload without the Karma runner actually navigating.
    reloadPage(): void {
        window.location.reload();
    }

    private async initializeAsync() {
        // Initialize root.
        if (isIe()) {
            document.body.classList.add("ie");          // Handle IE specific styling.
            objectFitImages();                          // Polyfill for "object-fit" and "object-position".
        }
        this.util.set(arcPathParams());                 // Get params from path. { applicationName, programName, renewalToken?, participantToken?, baseUrl }
        await this.exchangeCapabilityLinkIfPresent();
        await this.exchangeLegacyParticipantLinkIfPresent();
        await this.exchangeLegacySubmissionLinkIfPresent();
        await this.loadCapabilityContext();
        this.util.initItem(this.rootItem);              // Initialize root item.

        // Applied Pay return: cold redirect back from the hosted payment page. No SPA state exists, so render
        // the payment confirmation from the query string and skip the program/application bootstrap.
        if (this.util.store.programName === "payment-complete") {
            await this.renderAppliedPayReturn();
            return;
        }

        if (!await this.isProgramReady()) {
            return;
        }

        await this.loadProgramApplication();
        this.checkCapabilityExpiry();
    }

    private async loadCapabilityContext(): Promise<void> {
        if (sessionStorage.getItem("arc_capability_access_token") &&
            this.api?.isCapabilityExpiredOrExpiryUnknown?.()) {
            this.api.clearCurrentApplicantCapability?.();
            // If this page was opened from a capability link, exchange the
            // subject token again rather than sending the stale cached token.
            await this.exchangeCapabilityLinkIfPresent();
            await this.exchangeLegacyParticipantLinkIfPresent();
            await this.exchangeLegacySubmissionLinkIfPresent();
        }
        if (sessionStorage.getItem("arc_capability_access_token")) {
            try {
                const context = await this.api.getCapabilityContext();
                if (context) {
                    const workflow = this.getCapabilityWorkflow(context.operation, context.scopes);
                    this.util.set({ capabilityContext: context, capabilityWorkflow: workflow }, { batch: true });
                } else {
                    this.api.clearCurrentApplicantCapability?.();
                }
            } catch (_) {
                // A stale capability must not prevent public program bootstrap.
                // Link exchange will establish a new token when the URL carries one.
                this.api.clearCurrentApplicantCapability?.();
            }
        }
    }

    private async isProgramReady(): Promise<boolean> {
        const programOpen = await this.api.getProgramOpen();
        if (!programOpen) {
            this.errors.programOpenError();
            return false;
        }

        const opsApiStatus = await this.api.getOpsApiStatus();
        if (!opsApiStatus) {
            this.errors.opsApiStatusError();
            return false;
        }

        return true;
    }

    private async loadProgramApplication(): Promise<void> {
        // Load static applications before application form request.
        const { programName, applicationName } = this.util.store;
        const isStatic = this.staticPrograms[programName] || applicationName === "umbrella";    // Determine if application should use static data.
        const isPayment = location.pathname.indexOf("confirmpayment") > -1;                     // Determine if route is for payment (in which case dont display static ui).
        if (isStatic && !isPayment) {
            await this.loadApplication(true);                                                    // Load static application.
            const applicationFormResponse = await this.api.getApplicationForm();                 // Get application form.
            if (!applicationFormResponse) { return; }                                           // If no application form, exit.
            this.updateAppliedPayActive();                                                       // Use rollout/provider metadata loaded with the form.
            await this.associateApplicationData(true);                                           // Associate FormItems with RatingItems.
            this.util.setItemValue("loading", false);                                            // Backend metadata is ready; allow submit.
            this.util.setItemValue("error", "");                                                 // Hide error.
        }

        // Load other applications after application form request.
        if (!isStatic || isPayment) {
            var backendTimeout = setTimeout(this.errors.timeoutError, this.backendWaitTime);    // If wait time elapses, display timeout error.
            const applicationFormResponse = await this.api.getApplicationForm();                // Get application form from backend.
            if (!applicationFormResponse) { return; }                                           // If no application form, exit.
            this.updateAppliedPayActive();                                                       // Use rollout/provider metadata loaded with the form.
            this.util.setItemValue("error", "");                                                // Hide error.
            clearTimeout(backendTimeout);                                                       // Clear timeout error.
            await this.loadApplication();
            await this.associateApplicationData();                                               // Load application and handle renewal / participant token.
        }
    }

    private updateAppliedPayActive(): void {
        this.util.set({
            appliedPayActive: this.api.isAppliedPayProviderActiveForUi()
        }, { batch: true });
    }

    private getCapabilityWorkflow(operation: string, scopes: string[]): string {
        const allowed = {
            "quote:resume": "quote",
            "renewal:resume": "renewal",
            "referral:resume": "referral",
            "payment:initiate": "payment",
            "signature:complete": "signature"
        };
        if (!operation || !allowed[operation] || !scopes?.length) { return ""; }
        return allowed[operation];
    }

    private async exchangeCapabilityLinkIfPresent() {
        const subjectToken = new URLSearchParams(window.location.search).get("subject_token");
        if (!subjectToken || sessionStorage.getItem("arc_capability_access_token")) { return; }
        try {
            await this.api.exchangeCapabilityLink(subjectToken, sessionStorage.getItem("arc_capability_session") || undefined);
            const url = new URL(window.location.href);
            url.searchParams.delete("subject_token");
            window.history.replaceState({}, document.title, url.toString());
        } catch (_) {
            sessionStorage.removeItem("arc_capability_access_token");
        }
    }

    private async exchangeLegacyParticipantLinkIfPresent() {
        const participantToken = this.util.get("participantToken");
        if (!participantToken || sessionStorage.getItem("arc_capability_access_token")) { return; }
        try {
            const operation = this.util.get("legacyLinkOperation") || "quote:resume";
            await this.api.exchangeLegacyParticipantRatingLink(
                participantToken,
                operation,
                sessionStorage.getItem("arc_capability_session") || undefined);
            this.util.set({ participantToken: undefined }, { batch: true });
        } catch (_) {
            // Preserve the legacy fallback if the compatibility exchange fails.
            sessionStorage.removeItem("arc_capability_access_token");
        }
    }

    private async exchangeLegacySubmissionLinkIfPresent() {
        const params = new URLSearchParams(window.location.search);
        const submissionToken = params.get("id");
        if (!submissionToken || params.get("state") !== "sign" || sessionStorage.getItem("arc_capability_access_token")) { return; }
        try {
            await this.api.exchangeLegacySubmissionLink(
                submissionToken,
                "signature:complete",
                sessionStorage.getItem("arc_capability_session") || undefined);
            params.delete("id");
            const url = new URL(window.location.href);
            url.search = params.toString();
            window.history.replaceState({}, document.title, url.toString());
        } catch (_) {
            sessionStorage.removeItem("arc_capability_access_token");
        }
    }

    private async renderAppliedPayReturn() {
        const params = parseAppliedPayReturnParams();                                // Payment result from the redirect query string.
        const snapshot = this.actions.getAppliedPayReturnSnapshot();                 // Contact/Coverage/Premium saved before redirect.
        this.actions.clearAppliedPayReturnSnapshot();                                // Consume once; don't leave PII in the tab.

        let summary: any;
        let header: string;
        let email = "";
        if (snapshot) {
            try { summary = arcRegisterFormSummary(snapshot); } catch (e) { summary = undefined; }
            if (snapshot.programTitle) {
                try { header = getHeader(snapshot.baseUrl, snapshot.programTitle, snapshot.effectiveDates, snapshot.carrier); } catch (e) { header = undefined; }
            }
            email = snapshot.participant ? snapshot.participant.email : "";
        }
        // No session: arcPaymentComplete renders the thank-you line plus any URL-sourced details (Reference / Amount / Fee).

        const receiptNumberEnabled = (await this.api.getIsAppliedPayReceiptNumberEnabled().catch(() => false)) === true;  // Any failure or non-true answer keeps the referenceId.

        const item = arcPaymentComplete(params, { email, summary, header }, receiptNumberEnabled);  // Header + payment block + email + summary.
        this.util.initItem(item, { append: true, parent: this.rootItem });           // Render it under the root.
        this.util.setItemValue("loading", false);                                    // Hide the loading spinner.
        this.util.setItemValue("error", "");                                         // Ensure no error modal.
    }

    async loadApplication(deferLoading = false) {
        var { applicationFormResponse, programName, error } = this.util.store;

        if (!applicationFormResponse) {
            if (umbrellaVariables[programName]) {
                this.util.set(umbrellaVariables[programName]);
            }
            else {
                let mock: any = this.staticPrograms[programName];
                if (mock) { this.api.responseHandler("applicationForm", mock); }
            }
        }

        // Initialize captcha
        this.actions.initCaptcha();

        // Initialize application form item.
        const formItem = this.actions.initApplicationForm();                    // Format as infinity form item.
        if (error.value) { return; }                                                // Exit if errors in formatter.
        this.util.initItem(formItem, { append: true, parent: this.rootItem });  // Initialize form item and append to root item.

        // Hide loading spinner if not a renewal (otherwise wait for application form response).
        if (deferLoading || this.util.get("renewalToken")) { return; }
        setTimeout(() => {
            this.util.setItemValue("loading", false);
            this.util.setItemValue("error", "");
        }, 200);
    }

    async associateApplicationData(linkData?: boolean) {
        const { applicationFormResponse, ratingGroups } = this.util.store;

        this.linkStaticRatingData(ratingGroups, linkData);
        this.handleRenewalApplication(applicationFormResponse);

        const capability = this.getHydratableCapability();
        const canHydrateRating = await this.hydrateCapabilityRating(capability);
        const canHydrateSubmission = await this.hydrateCapabilitySubmission(capability, canHydrateRating);
        await this.hydrateLegacyParticipantRating(canHydrateRating, canHydrateSubmission);

        this.finishRenewalLoading();
    }

    private linkStaticRatingData(ratingGroups: any, linkData = false): void {
        if (!linkData) { return; }
        const allItems = arcRatingGroups(ratingGroups).reduce(this.util.itemReducer, []);
        allItems.forEach(ratingItem => {
            const staticItem = this.util.store[ratingItem.itemId];
            if (staticItem) { staticItem.data = ratingItem.data; }
        });
    }

    private handleRenewalApplication(applicationFormResponse: any): void {
        if (applicationFormResponse.renewal !== true) { return; }
        this.util.set({ showLandingPage: false }, { batch: true });
        if (this.util.get("programEnrollment.program.autoPopulateParticipantInfo", applicationFormResponse)) {
            this.actions.prefillParticipant(applicationFormResponse.participant);
            return;
        }

        const email = this.util.get("email");
        email.value = this.util.get("participant.email", applicationFormResponse);
        email.disabled = true;
        this.util.set({ email }, { batch: true });
    }

    private getHydratableCapability(): { ratingId: any; submissionId: any; operation: string; scopes: string[] } {
        const operation = this.util.get("capabilityContext.operation");
        const scopes = this.util.get("capabilityContext.scopes") || [];
        const canReadIntake = scopes.indexOf("intake:read") >= 0 || scopes.indexOf("intake:resume") >= 0;
        const canReadOperation = ["quote:resume", "renewal:resume", "referral:resume", "signature:complete"].indexOf(operation) >= 0;
        return {
            ratingId: canReadIntake && canReadOperation ? this.util.get("capabilityContext.participantRatingId") : null,
            submissionId: canReadIntake && canReadOperation ? this.util.get("capabilityContext.submissionExternalId") : null,
            operation,
            scopes
        };
    }

    private async hydrateCapabilityRating(capability: { ratingId: any }): Promise<boolean> {
        if (!capability.ratingId) { return false; }
        const rating = await this.api.getV2ParticipantRating(capability.ratingId);
        if (!rating) { return true; }
        const normalized = normalizeParticipantRatingForIntake(rating);
        this.util.set({
            v2ParticipantRatingResponse: rating,
            participantRatingSummaryResponse: normalized,
            participantRatingId: rating.participantRatingId || rating.id || capability.ratingId
        }, { batch: true });
        if (normalized.itemSummaries) { this.actions.addSummaryToPaymentPage(); }
        return true;
    }

    private async hydrateCapabilitySubmission(
        capability: { submissionId: any },
        canHydrateRating: boolean): Promise<boolean> {
        if (canHydrateRating || !capability.submissionId) { return false; }
        const submission = await this.api.getV2Submission(capability.submissionId);
        if (submission) { this.util.set({ v2SubmissionResponse: submission }, { batch: true }); }
        return true;
    }

    private async hydrateLegacyParticipantRating(canHydrateRating: boolean, canHydrateSubmission: boolean): Promise<void> {
        if (canHydrateRating || canHydrateSubmission || !this.util.store.participantToken) { return; }
        const participantRatingSummary = await this.api.getParticipantRatingSummary();
        if (participantRatingSummary) { this.actions.addSummaryToPaymentPage(); }
    }

    private finishRenewalLoading(): void {
        if (!this.util.get("renewalToken")) { return; }
        setTimeout(() => {
            this.util.setItemValue("loading", false);
            this.util.setItemValue("error", "");
        }, 200);
    }

}
